// displayedMatchSnapshot — wraps matchSnapshot adicionando "hold"
// quando uma vaza fecha. UX: quando o 4o player joga (ou 2o em 1v1),
// queremos que o user veja todas as cartas na mesa por uns segundos
// antes do snap seguinte (que pode limpar tudo) ser aplicado.
//
// Os modais (Truco, Finished) continuam usando matchSnapshot LIVE —
// queremos que eles respondam imediato. So o visual da mesa (plays,
// cross) usa displayedMatchSnapshot.

import { writable, get } from 'svelte/store';
import { matchSnapshot } from './match';
import type { MatchSnapshot, PlayerSnapshot } from './match';

// Tempo de hold quando vaza fecha — user pediu "uns 8 segundos pra
// ver a carta". 5s eh um meio termo confortavel (suficiente pra ler
// a carta sem deixar partida arrastada).
const HOLD_MS = 5000;

export const displayedMatchSnapshot = writable<MatchSnapshot | null>(null);

let pendingSnap: MatchSnapshot | null = null;
let holdTimer: number | null = null;

// clearHold — cancela o hold-timer e aplica o snap LIVE imediatamente.
// Usado pela cinematic D quando termina a anim de recolher: nao queremos
// que os slots originais reapparecam mostrando as 4 cartas (hold ainda
// nao expirou). Forcamos o avanco pro snap LIVE (que ja eh a proxima vaza).
export function clearHold(): void {
  if (holdTimer !== null) {
    clearTimeout(holdTimer);
    holdTimer = null;
  }
  const live = get(matchSnapshot);
  if (live) {
    displayedMatchSnapshot.set(live);
  }
  pendingSnap = null;
}

// Conta quantos slots de play estao COMPLETOS (todos os players
// tem carta naquele slot). Em 2v2: 4 players, slot completo = 4
// cartas. Em 1v1: 2.
function fullSlotsCount(snap: MatchSnapshot): number {
  if (snap.format === '2v2') {
    const players = [...(snap.team_a || []), ...(snap.team_b || [])];
    const target = players.length;
    if (target === 0) return 0;
    return ['first_play', 'second_play', 'third_play'].filter(
      (slot) => players.filter((p) => (p as Record<string, unknown>)[slot] && ((p as Record<string, { value?: string }>)[slot] as { value?: string }).value).length >= target,
    ).length;
  }
  // 1v1
  const players: PlayerSnapshot[] = [snap.p1, snap.p2].filter(Boolean) as PlayerSnapshot[];
  return ['first_play', 'second_play', 'third_play'].filter(
    (slot) => players.filter((p) => {
      const c = (p as Record<string, { value?: string } | undefined>)[slot];
      return c && c.value;
    }).length >= players.length,
  ).length;
}

// Trick acabou de fechar quando fullSlotsCount aumentou entre prev
// e next. Tambem: id mudou (nova partida) — nao hold.
function justClosedTrick(prev: MatchSnapshot, next: MatchSnapshot): boolean {
  if (prev.id !== next.id) return false;
  return fullSlotsCount(next) > fullSlotsCount(prev);
}

// Subscribe global no matchSnapshot pra orquestrar o hold.
matchSnapshot.subscribe((snap) => {
  if (!snap) {
    displayedMatchSnapshot.set(null);
    pendingSnap = null;
    if (holdTimer !== null) {
      clearTimeout(holdTimer);
      holdTimer = null;
    }
    return;
  }

  // Estamos em hold — guarda o snap incoming como pending,
  // displayed nao muda ate o timer expirar.
  if (holdTimer !== null) {
    pendingSnap = snap;
    return;
  }

  const current = get(displayedMatchSnapshot);

  // Se eh um snap "trick fechou" relativo ao atual displayed,
  // abre janela de hold: aplica o snap (mostrando as cartas) e
  // bloqueia novos updates por HOLD_MS.
  if (current && justClosedTrick(current, snap)) {
    displayedMatchSnapshot.set(snap);
    pendingSnap = null;
    holdTimer = window.setTimeout(() => {
      // Apos hold, aplica o snap mais recente que tinha chegado
      // durante o hold (ou mantem o atual se nada chegou).
      if (pendingSnap) {
        displayedMatchSnapshot.set(pendingSnap);
        pendingSnap = null;
      }
      holdTimer = null;
    }, HOLD_MS);
  } else {
    displayedMatchSnapshot.set(snap);
  }
});
